import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  SelectBooleanInput,
  NumberInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { CardBlock } from '../../../organisms/card';

type Threshold = {
  max?: number;
  avg?: number;
  min?: number;
};

interface EpxHyperSettingsData {
  alerts?: boolean;
  afterHourInfo?: boolean;
  thresholds?: {
    red?: {
      upper: Threshold;
      lower: Threshold;
    };
    yellow?: {
      upper: Threshold;
      lower: Threshold;
    };
    green?: {
      upper: Threshold;
      lower: Threshold;
    };
    heartRate?: {
      min?: number;
      max?: number;
    };
    medRecheckDuration?: number;
  };
}

const DefaultSettings: EpxHyperSettingsData = {
  alerts: true,
  afterHourInfo: false,
  thresholds: {
    red: {
      upper: {
        max: 180,
        avg: 160,
        min: 90
      },
      lower: {
        max: 110,
        avg: 100,
        min: 60
      }
    },
    yellow: {
      upper: {
        avg: 140
      },
      lower: {
        avg: 90
      }
    },
    heartRate: {
      min: 60,
      max: 100
    },
    medRecheckDuration: 4
  }
};

interface EpxHyperSettingsProps {
  setForm?: (form: FormInstance<EpxHyperSettingsData>) => void;
  onSubmit?: (data: EpxHyperSettingsData) => void;
  disabled?: boolean;
}

const EpxHyperSettings = ({
  setForm,
  onSubmit: _onSubmit,
  disabled
}: EpxHyperSettingsProps): JSX.Element => {
  const form = new FormInstance<EpxHyperSettingsData>(DefaultSettings);
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: EpxHyperSettingsData): void => {
    form.setData(data);
  };
  const onSubmit = (data: EpxHyperSettingsData): void => {
    _onSubmit(data);
  };
  return (
    <CardBlock bare>
      <Form form={form} onChange={onChange} onSubmit={onSubmit}>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Enable Alerts for Patient"
                name="alerts"
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <SelectBooleanInput
                label="Inform Business Hour"
                name="afterHourInfo"
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Red - Systolic Avg"
                name="thresholds.red.upper.avg"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Red - Systolic Max"
                name="thresholds.red.upper.max"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Red - Systolic Min"
                name="thresholds.red.upper.min"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Red - Diastolic Avg"
                name="thresholds.red.lower.avg"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Red - Diastolic Max"
                name="thresholds.red.lower.max"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Red - Diastolic Min"
                name="thresholds.red.lower.min"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Yellow - Systolic Avg"
                name="thresholds.yellow.upper.avg"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Yellow - Diastolic Avg"
                name="thresholds.yellow.lower.avg"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Heart Rate Min"
                name="thresholds.heartRate.min"
                required
                fill
                disabled={disabled}
              />
            </Col>
            <Col sm={12}>
              <NumberInput
                label="Heart Rate Max"
                name="thresholds.heartRate.max"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={12}>
              <NumberInput
                label="Medication Recheck Duration"
                name="thresholds.medRecheckDuration"
                required
                fill
                disabled={disabled}
              />
            </Col>
          </Row>
        </CardBlock>
      </Form>
    </CardBlock>
  );
};

export default EpxHyperSettings;
